import { BillStatusEnum, PaymentStatusEnum, type Bill, type LedgerEntry } from '@/types/composition-functions';
import { PAYMENT_STATUS_FILTER_OPTIONS } from '@/composables/billing/usePayments';
import {
  approvalStatusLabel,
  billStatusLabel,
  billableTypeName,
  humanizeCode,
  isCode,
  normalizeCode,
  paymentStatusLabel,
} from '@/utils/billingLabels';
import { billNumberLabel } from '@/utils/billNumberLabel';

/**
 * Presentation decisions for a student's money: the Student account page, the
 * students directory, the peek drawer and the debtors list.
 */

/**
 * Share of what the student has to pay that is already paid.
 *
 * Deliberately paid / (paid + balance owed), not paid / total billed: for class
 * bills total billed is the amount BEFORE discounts and exemptions, so a
 * student on a scholarship who has paid everything showed "80% paid" next to
 * a ₦0 balance.
 */
export function paidPercent(paid: number | null | undefined, balanceOwed: number | null | undefined): number {
  const safePaid = Math.max(0, paid ?? 0);
  const total = safePaid + Math.max(0, balanceOwed ?? 0);
  if (total <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((safePaid / total) * 100)));
}

/**
 * The payments a drawer should list for the student it is open on.
 *
 * Apollo keeps the previous result while the next student's payments load,
 * and keeps it for good when that load fails, so a drawer reading the raw
 * result showed the last student's payments under the new student's name.
 * Only rows that belong to the open student are returned.
 */
export function paymentsOfStudent<T extends { student_id?: string | number | null }>(
  payments: readonly T[] | null | undefined,
  studentId: string | number | null | undefined,
): T[] {
  if (studentId === null || studentId === undefined || studentId === '') return [];
  return (payments ?? []).filter(payment => String(payment.student_id ?? '') === String(studentId));
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * One readable date format everywhere: "6 Oct 2026".
 * A date-only value ("2026-10-06") is read as a local calendar day so it never
 * shifts by a day in time zones behind UTC.
 */
export function formatDisplayDate(value: string | null | undefined, fallback = '—'): string {
  if (!value) return fallback;
  const dateOnly = DATE_ONLY.exec(value);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "2026-03" -> "Mar 2026" (revenue chart months). */
export function formatMonthLabel(value: string | null | undefined): string {
  const match = /^(\d{4})-(\d{2})$/.exec(value ?? '');
  if (!match) return value ?? '';
  return new Date(Number(match[1]), Number(match[2]) - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

/** "today" / "yesterday" / "5 days ago" / "3 months ago" / "2 years ago". */
export function relativeDateLabel(value: string | null | undefined, now: Date = new Date()): string {
  if (!value) return '';
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return '';
  const days = Math.floor((now.getTime() - then) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  if (days < 365) {
    const months = Math.max(1, Math.floor(days / 30));
    return `${months} month${months > 1 ? 's' : ''} ago`;
  }
  const years = Math.floor(days / 365);
  return `${years} year${years > 1 ? 's' : ''} ago`;
}

// ─── Placement ───

interface ClassSectionLike {
  classroom?: { name?: string | null } | null;
  section?: { name?: string | null } | null;
}

/**
 * "JSS1 (A)", or just "400" when the section has no name — never "400 ()".
 * Empty string when there is no class to name.
 */
export function classSectionLabel(cs: ClassSectionLike | null | undefined): string {
  const classroom = cs?.classroom?.name?.trim();
  if (!classroom) return '';
  const section = cs?.section?.name?.trim();
  return section ? `${classroom} (${section})` : classroom;
}

// ─── Bills ───

interface BillDetailLike {
  item_name?: string | null;
  billItemTemplate?: { name?: string | null } | null;
}

type Nullable<T> = { [K in keyof T]?: T[K] | null };

type BillTitleLike = Pick<Bill, 'id'> & Nullable<Pick<Bill, 'invoice_number'>> & {
  billDetails?: ReadonlyArray<BillDetailLike | null> | null;
};

/** "Tuition, Lab fee +2 more", or '' when the bill carries no named fee. */
function feeNames(bill: BillTitleLike): string {
  const names = (bill.billDetails ?? [])
    .map((detail) => detail?.item_name || detail?.billItemTemplate?.name || '')
    .filter((name) => name.length > 0);
  if (names.length === 0) return '';
  if (names.length <= 2) return names.join(', ');
  return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
}

/**
 * A bill named by its fees ("Tuition, Lab fee +2 more"), so rows can be told
 * apart; a bill with no named fee is called by its invoice number.
 */
export function billTitle(bill: BillTitleLike): string {
  return feeNames(bill) || billNumberLabel(bill);
}

/**
 * "INV-000007 · School fees": the title for a bill that has no term or session
 * to be named after. The invoice number is what the payer sees on the PDF and
 * the receipt, so it leads; the fees say what the money is for.
 */
export function billNumberAndFeesTitle(bill: BillTitleLike): string {
  const fees = feeNames(bill);
  const number = billNumberLabel(bill);
  return fees ? `${number} · ${fees}` : number;
}

interface BilledToLike {
  billable_type?: string | null;
  billable?: Record<string, unknown> | null;
}

function cohortName(billable: Record<string, unknown> | null | undefined): string {
  if (!billable) return '';
  const placement = classSectionLabel(billable as ClassSectionLike);
  if (placement) return placement;
  if (typeof billable.program_name === 'string') return billable.program_name;
  if (typeof billable.name === 'string') return billable.name;
  return '';
}

/** "Billed to: Class section · JSS1 (A)" / "Billed to: This student only". */
export function billedToLabel(bill: BilledToLike): string {
  if (isCode(bill.billable_type, 'Student')) return 'Billed to: This student only';
  const type = billableTypeName(bill.billable_type) || 'Group';
  const name = cohortName(bill.billable);
  return name ? `Billed to: ${type} · ${name}` : `Billed to: ${type}`;
}

/** Bill statuses a bursar filters a student's bills by. Values are the GraphQL enum names the API expects. */
export const BILL_STATUS_FILTER_OPTIONS: Array<{ label: string; value: BillStatusEnum }> = [
  BillStatusEnum.Published,
  BillStatusEnum.PartiallyPaid,
  BillStatusEnum.Paid,
  BillStatusEnum.Overdue,
].map((value) => ({ label: billStatusLabel(value, 'staff'), value }));

// ─── Payments ───

/** Payment statuses for the client-side filter, worded like the status tags. */
export const PAYMENT_STATUS_OPTIONS: Array<{ label: string; value: PaymentStatusEnum }> = PAYMENT_STATUS_FILTER_OPTIONS
  .map((option) => ({ label: paymentStatusLabel(option.value), value: option.value }));

/** Whether a fetched payment matches the chosen status, whatever case the API used. */
export function matchesPaymentStatus(status: string | null | undefined, filter: string | null | undefined): boolean {
  return !filter || isCode(status, filter);
}

interface VoidablePayment {
  payment_channel?: string | null;
  status?: string | null;
}

/** Only successful payments a staff member recorded by hand can be cancelled from here. */
export function canCancelPayment(payment: VoidablePayment): boolean {
  return isCode(payment.payment_channel, 'manual') && isCode(payment.status, PaymentStatusEnum.Successful);
}

// ─── Ledger ───

type LedgerKeyLike = Partial<Pick<LedgerEntry, 'related_type' | 'related_id'>> & { type?: string | null };

/**
 * Unique row key for a page of statement lines. related_id alone repeats:
 * bill #12 and payment #12 share it, and every discount line carries its
 * discount category's id. Entries have no id of their own, so the line's
 * position on the page makes the key unique.
 */
export function ledgerRowKey(entry: LedgerKeyLike, index: number): string {
  return `${normalizeCode(entry.type)}:${normalizeCode(entry.related_type)}:${entry.related_id ?? ''}:${index}`;
}

/** Status words for a ledger line depend on what the line is. */
export function ledgerStatusLabel(type: string | null | undefined, status: string | null | undefined): string {
  if (!status) return '';
  const kind = normalizeCode(type);
  if (kind === 'payment') return paymentStatusLabel(status);
  if (kind === 'bill') return billStatusLabel(status, 'staff');
  if (kind === 'exemption' || kind === 'refund') return approvalStatusLabel(status);
  return humanizeCode(status);
}

/** Words under the running balance. */
export function runningBalanceLabel(balance: number): string {
  if (balance > 0.005) return 'owes';
  if (balance < -0.005) return 'in credit';
  return 'fully paid';
}
